import { SeverityLevel } from '@logz-pkg/enums';
import { merge } from 'lodash';
import { graphDarkV2Theme } from '../../packages/graphs/src/Graph/themes/dark-v2.theme';
import { badgeDarkTheme } from '../Badge/themes/dark-v2.theme';
import { breadcrumbsDarkTheme } from '../Breadcrumbs/themes/dark.theme';
import { buttonDarkTheme } from '../Button/themes/dark-v2.theme';
import { buttonGroupDarkTheme } from '../ButtonGroup/themes/dark.theme';
import { cardDarkTheme } from '../Card/themes/dark.theme';
import { checkboxDarkTheme } from '../Checkbox/themes/dark.theme';
import { collapseDarkTheme } from '../Collapse/themes/dark.theme';
import { containerDarkV2Theme } from '../containers/Container/themes/dark-v2.theme';
import { fieldContainerDarkTheme } from '../containers/FieldContainer/themes/dark.theme';
import { pageDarkTheme } from '../containers/Page/themes/dark.theme';
import { dividerDarkTheme } from '../Divider/themes/dark.theme';
import { drawerDarkTheme } from '../Drawer/themes/dark-v2.theme';
import { filterDarkThemeV2 } from '../Filter/themes/dark-v2.theme';
import { headerDarkV2Theme } from '../Header/themes/dark-v2.theme';
import { infoDarkTheme } from '../Info/themes/dark.theme';
import { inlineNotificationDarkTheme } from '../InlineNotification/themes/dark.theme';
import { searchDarkTheme } from '../Input/Search/themes/dark.theme';
import { inputDarkTheme } from '../Input/themes/dark.theme';
import { selectDarkTheme } from '../inputs/Select/themes/dark.theme';
import { menuDarkTheme } from '../menu/themes/dark.theme';
import { modalDarkV2Theme } from '../Modal/themes/dark.theme';
import { multiTagDarkTheme } from '../MultiTag/themes/dark.theme';
import { NotificationDarkTheme } from '../Notification/theme/dark.theme';
import { highlightDarkTheme } from '../Notifications & Messages/themes/dark.theme';
import { popMenuDarkTheme } from '../popMenu/themes/dark.theme';
import { popoverDarkTheme } from '../popover/theme/dark.theme';
import { linearProgressDarkTheme } from '../progress/LinearProgress/themes/dark.theme';
import { spinnerDarkTheme } from '../progress/Spinner/themes/dark.theme';
import { radioDarkTheme } from '../Radio/themes/dark.theme';
import { scrollSnappingDarkTheme } from '../ScrollSnapping/themes/dark.theme';
import { searchFiltersDarkTheme } from '../SearchFilters/themes/dark.theme';
import { primarySidenavDarkTheme } from '../Sidenav/Primary/themes/dark.theme';
import { secondarySidenavDarkTheme } from '../Sidenav/Secondary/themes/dark-v2.theme';
import { tableDarkTheme } from '../table/Crud/theme/dark.theme';
import { tabsDarkTheme } from '../Tabs/themes/dark-v2.theme';
import { tagDarkTheme } from '../Tag/themes/dark.theme';
import { toggleDarkTheme } from '../Toggle/themes/dark.theme';
import { tooltipDarkTheme } from '../tooltip/themes/dark.theme';
import { linkDarkTheme } from '../typography/Link/themes/dark-v2.theme';
import { textDarkV2Theme } from '../typography/Text/themes/dark-v2.theme';
import { titleDarkTheme } from '../typography/Title/themes/dark.theme';
import { opacity } from '../utils';
import { superDatePickerDarkTheme } from '../../packages/dates-legacy/src/SuperDateRangePicker/themes/dark-v2.theme';
import { colorsDark } from './dark-colors';
import { colorsV2Dark } from './dark-colors-v2';
import { lightV2Theme } from './light-v2.theme';

export const darkV2Theme = merge({}, lightV2Theme, {
  name: 'dark',
  shadows: {
    none: 'none',
    line: '0 -1px 0px 0 rgba(42, 45, 46) inset',
    small: '0 2px 6px 0 rgba(0, 0, 0, 0.5)',
    large: '0 10px 10px rgba(0, 0, 0, 0.3), 0 5px 25px rgba(0, 0, 0, 0.3)',
  },
  fonts: {
    text: 'Roboto, sans-serif',
    code: "'Roboto Mono', monospace",
    title: 'Ubuntu, sans-serif',
  },
  colors: {
    ...colorsDark,
    background: colorsDark.gray[0],
    border: colorsDark.gray[400],

    actions: {
      primary: colorsDark.blue[500],
      secondary: opacity(colorsDark.blue[200], 10),
      accent: colorsDark.orange[500],
    },
    semantic: {
      accent: colorsDark.orange[500],
      announcement: colorsDark.gray[800],
      danger: colorsDark.red[500],
      info: colorsDark.gray[800],
      notice: colorsDark.gray[600],
      success: colorsDark.green[500],
      warning: colorsDark.orange[400],
      critical: colorsDark.gray[0],
    },
    severity: {
      [SeverityLevel.Info]: colorsDark.blue[600],
      [SeverityLevel.Low]: colorsDark.green[600],
      [SeverityLevel.Medium]: colorsDark.orange[600],
      [SeverityLevel.High]: colorsDark.red[600],
      [SeverityLevel.Severe]: colorsDark.magenta[600],
    },
  },
  colorsV2: colorsV2Dark,
  text: textDarkV2Theme,
  button: buttonDarkTheme,
  link: linkDarkTheme,
  card: cardDarkTheme,
  table: tableDarkTheme,
  container: containerDarkV2Theme,
  page: pageDarkTheme,
  divider: dividerDarkTheme,
  header: headerDarkV2Theme,
  radio: radioDarkTheme,
  badge: badgeDarkTheme,
  inlineNotification: inlineNotificationDarkTheme,
  highlight: highlightDarkTheme,
  buttonGroup: buttonGroupDarkTheme,
  popMenu: popMenuDarkTheme,
  modal: modalDarkV2Theme,
  input: inputDarkTheme,
  linearProgress: linearProgressDarkTheme,
  searchFilters: searchFiltersDarkTheme,
  scrollSnapping: scrollSnappingDarkTheme,
  spinner: spinnerDarkTheme,
  checkbox: checkboxDarkTheme,
  tooltip: tooltipDarkTheme,
  title: titleDarkTheme,
  fieldContainer: fieldContainerDarkTheme,
  tabs: tabsDarkTheme,
  info: infoDarkTheme,
  select: selectDarkTheme,
  toggle: toggleDarkTheme,
  search: searchDarkTheme,
  primarySidenav: primarySidenavDarkTheme,
  secondarySidenav: secondarySidenavDarkTheme,
  breadcrumbs: breadcrumbsDarkTheme,
  multiFilter: filterDarkThemeV2,
  collapse: collapseDarkTheme,
  multiTag: multiTagDarkTheme,
  tag: tagDarkTheme,
  menu: menuDarkTheme,
  notification: NotificationDarkTheme,
  drawer: drawerDarkTheme,
  popover: popoverDarkTheme,
  graph: graphDarkV2Theme,
  backdrop_background_color: 'rgba(0, 0, 0, 0.5)',
  datePicker: superDatePickerDarkTheme,
});
